import { CURRENT_JSON_VERSION, JsonStructure } from "db/definitions";
import { createFieldForBatchResolver } from "db/queries/fieldInstanceQueries";
import { v4 as uuid } from "uuid";
import { _getFormGroupDocument } from "./_getFormGroupDocument";
import { batchGetFieldInstancesByFormGroup } from "./batchGetFieldInstancesByFormGroup";
import { batchGetFormInstancesByFormGroup } from "./batchGetFormInstancesByFormGroup";
import { batchGetImagesByFormGroup } from "./batchGetImagesByFormGroup";
import { handleImageObjCopy } from "./batchUploadFromPhotolog";

export async function duplicateFormGroups(formGroupIds: string[], online: boolean): Promise<{ json: JsonStructure; formGroups: string[] }> {
	const final: JsonStructure = {
		projects: [],
		formGroups: [],
		formInstances: [],
		formTemplates: [],
		fieldInstances: [],
		revisions: [],
		fieldTemplates: [],
		options: [],
		continuationDatas: [],
		images: [],
		version: CURRENT_JSON_VERSION,
	};
	const duplicatedFormGroups: string[] = [];
	for (const formGroupId of formGroupIds) {
		// These are mapping the old ids to the new ids
		const formInstanceMap: Map<string, string> = new Map();
		const fieldInstanceMap: Map<string, string> = new Map();
		const [formGroup, formInstances, fieldInstances, images] = await Promise.all([
			_getFormGroupDocument(formGroupId, online),
			batchGetFormInstancesByFormGroup(formGroupId, online),
			batchGetFieldInstancesByFormGroup(formGroupId, online),
			batchGetImagesByFormGroup(formGroupId, online),
			// Should we ever want to duplicate the revisions in the formGroup, we need to retrieve them first, and loop over them later (also commented out)
			// getRevisionsByFormGroup(formGroupId, online)
		]);
		if (formGroup) {
			const formGroupCopy = duplicate(formGroup);
			// Duplicate formInstances, replacing the id of each on in the formGroup's formOrder
			for (const formInstance of formInstances) {
				const copy = duplicate(formInstance, formInstanceMap);
				copy.formGroup = formGroupCopy.id;
				final.formInstances.push(copy);
				const index = formGroupCopy.formOrder.findIndex((formInstanceId) => formInstanceId === formInstance.id);
				if (index !== -1) {
					formGroupCopy.formOrder.splice(index, 1, copy.id);
				} else {
					// For some reason, the formInstance we retrieved was not in the formGroup's formOrder. Just append to the end
					// and they can deal with it
					formGroupCopy.formOrder.push(copy.id);
				}
			}
			// Duplicate images, mapping old ids to new ids so we can tie the fieldInstances to them
			const oldImageIdToNewImageId: Map<string, string> = new Map();
			for (const image of images) {
				const imageCopy = duplicate(image);
				oldImageIdToNewImageId.set(image.id, imageCopy.id);
				// make sure the form group is using the new one.
				imageCopy.formGroup = formGroupCopy.id;
				const copiedKey = await handleImageObjCopy(imageCopy, online);
				imageCopy.key = copiedKey;
				final.images.push(imageCopy);
			}
			// Duplicate fieldInstances, replacing the formInstance ids with the new formInstance ids
			const tableFields = [];
			const altInstances = [];
			const parentTableFields = [];
			for (const fieldInstance of fieldInstances) {
				const copy = duplicate(fieldInstance, fieldInstanceMap);
				if (fieldInstance.imageId) {
					const newImageId = oldImageIdToNewImageId.get(fieldInstance.imageId);
					if (newImageId) copy.imageId = newImageId;
				}
				const fieldInstanceForResolver = createFieldForBatchResolver(copy);
				fieldInstanceForResolver.formGroup = formGroupCopy.id;
				// Table field children and altInstances won't have this, and shouldn't have this
				if (fieldInstanceForResolver.formInstance) {
					const formInstanceId = formInstanceMap.get(fieldInstanceForResolver.formInstance);
					// Should be superfluous, just making typescript happy with this if statement
					if (formInstanceId) fieldInstanceForResolver.formInstance = formInstanceId;
				}
				if (fieldInstanceForResolver.tableField) tableFields.push(fieldInstanceForResolver);
				if (fieldInstance.altInstanceId) altInstances.push(fieldInstanceForResolver);
				if (fieldInstanceForResolver.fieldsOrder) parentTableFields.push(fieldInstanceForResolver);
				final.fieldInstances.push(fieldInstanceForResolver);
			}
			// Give table cells the new parent id
			for (const tableField of tableFields) {
				// Makes typescript happy
				if (tableField.tableField) {
					const newParentId = fieldInstanceMap.get(tableField.tableField);
					// This should exist since we just looped through everything
					if (newParentId) tableField.tableField = newParentId;
				}
			}
			// Give parents the correct fieldsOrder
			for (const parentTableField of parentTableFields) {
				// Typescript check
				if (parentTableField.fieldsOrder) {
					const newFieldsOrder = [];
					for (const csv of parentTableField.fieldsOrder) {
						const ids = csv.split(",");
						let newRowOrder = "";
						for (const id of ids) {
							const newId = fieldInstanceMap.get(id);
							if (newId) newRowOrder += newRowOrder ? `,${newId}` : newId;
						}
						newFieldsOrder.push(newRowOrder);
					}
					parentTableField.fieldsOrder = newFieldsOrder;
				}
			}
			// Loop through linked fields, giving them the correct altInstance id
			for (const altInstance of altInstances) {
				// Make ts happy
				if (altInstance.altInstanceId) {
					const newAltId = fieldInstanceMap.get(altInstance.altInstanceId);
					if (newAltId) altInstance.altInstanceId = newAltId;
				}
			}
			formGroupCopy.name = `${formGroupCopy.name.trim()} (copy)`;
			final.formGroups.push(formGroupCopy);
			duplicatedFormGroups.push(formGroupCopy.id);
		}
	}
	return {
		json: final,
		formGroups: duplicatedFormGroups,
	};
}

function duplicate<T extends { id: string }>(document: T, map?: Map<string, string>): T {
	const id = uuid();
	map?.set(document.id, id);
	const copy = { ...document, id };
	return copy;
}
